import "./App.css";
import React, { useState } from "react";
import { write, useSnap } from "./store";
import toast, { Toaster } from "react-hot-toast";
import posthog from 'posthog-js';
// import Modal from "./components/Modal";
import Webcam from "react-webcam";
import NewModal from "./components/newModal";
import { locale } from "./components/locale";
import { validateAudioTrack } from "./utils/audioDeviceValidation";

let micTestInterval;

export function isNextEnabled(camStatus, micStatus, tos1, tos2, welcome) {
  return camStatus === 'granted' && micStatus === 'granted' && tos1 === true && tos2 === true && welcome === true;
}

const errorToStatus = {
  'NotAllowedError':       'denied',
  'PermissionDeniedError': 'denied',
  'NotFoundError':         'no_device',
  'DevicesNotFoundError':  'no_device',
  'NotReadableError':      'busy',
  'TrackStartError':       'busy',
  'OverconstrainedError':  'error',
  'TypeError':             'unsupported',
};

export function mapErrorToStatus(errorName) {
  return errorToStatus[errorName] ?? 'error';
}

export function getMobileOperatingSystem(ua = navigator.userAgent || navigator.vendor || window.opera) {
  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(ua)) return "Windows Phone";
  if (/android/i.test(ua)) return "Android";
  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(ua) && !window.MSStream) return "iOS";
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return "iOS";
  return "PC";
}

export function filterDevices(deviceList) {
  const webcams = (deviceList || []).filter(d => d.kind === 'videoinput');
  const mics = (deviceList || []).filter(d => d.kind === 'audioinput');
  return { webcams, mics };
}

const recoverableStatuses = ['denied', 'no_device', 'busy', 'error'];

/**
 * StatusBadge — pill badge shown on the right of each device section header.
 */
function StatusBadge({ status, label }) {
  if (status === 'granted') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
        </svg>
        {label} ready
      </span>
    );
  }
  if (status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" className="w-3.5 h-3.5 animate-spin" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        Checking...
      </span>
    );
  }
  if (status === 'denied') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5" aria-hidden="true">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
        </svg>
        Access denied
      </span>
    );
  }
  if (status === 'no_device') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        No device found
      </span>
    );
  }
  if (status === 'busy') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        Device in use
      </span>
    );
  }
  if (status === 'unsupported') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700">
        Not supported
      </span>
    );
  }
  if (status === 'error') {
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
        Error
      </span>
    );
  }
  // idle
  return (
    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500">
      Waiting...
    </span>
  );
}

/**
 * StatusIndicator — inline message shown below the section header for non-granted states.
 */
function StatusIndicator({ status, label }) {
  const messages = {
    idle:        null, // nothing to show while idle
    pending:     null, // badge is enough
    granted:     null, // badge is enough
    denied:      `${label} access was denied. Click "Try Again" or enable it in your browser settings.`,
    no_device:   `No ${label.toLowerCase()} found. Please connect one and refresh.`,
    busy:        `${label} is in use by another app. Close other apps and try again.`,
    unsupported: `${label} is not supported in this browser. Use Chrome, Firefox, or Edge.`,
    error:       `${label} error. Please refresh the page.`,
  };

  const msg = messages[status];
  if (!msg) return null;

  return (
    <p className="text-sm text-gray-500 mt-1" role="alert">{msg}</p>
  );
}

function DeviceSelector({ devices, selected, about, onChange }) {
  const isCam = about === 'cam';
  return (
    <div className="relative mt-3 flex min-w-0 items-center overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      {/* device-type icon prefix */}
      <span className="flex-shrink-0 pl-3 pr-2 text-slate-400">
        {isCam ? (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
          </svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
          </svg>
        )}
      </span>
      <select
        className="min-h-[44px] min-w-0 flex-1 appearance-none bg-transparent py-2.5 pr-9 text-sm text-slate-700 focus:outline-none"
        aria-label={`Select ${isCam ? 'webcam' : 'microphone'}`}
        onChange={onChange}
        about={about}
        value={selected || ''}
      >
        {devices.map((el) => (
          <option value={el.deviceId} key={el.deviceId}>
            {el.label}
          </option>
        ))}
      </select>
      {/* chevron */}
      <span className="pointer-events-none absolute right-3 text-slate-400">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </span>
    </div>
  );
}

function PermissionRequirement({ type, status, onRetry }) {
  const isGranted = status === 'granted';
  const isCamera = type === 'camera';
  const canRetry = recoverableStatuses.includes(status);

  return (
    <div className="flex items-center gap-3 rounded-xl border border-blue-100 bg-white px-3 py-2.5 shadow-sm">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-700">
          {isCamera ? (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5" aria-hidden="true">
              <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
            </svg>
          ) : (
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-5 w-5" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
          )}
        </span>
        <span className="min-w-0 text-sm font-semibold leading-5 text-slate-700">
          {isCamera ? 'Camera' : 'Microphone'} Access {isGranted ? 'Granted' : 'Required'}
        </span>
      </div>
      {canRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="min-h-[40px] flex-shrink-0 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200"
        >
          Try Again
        </button>
      )}
    </div>
  );
}

function MediaAccessOverlay({ camStatus, micStatus, onRetryCamera, onRetryMic }) {
  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[1.75rem] bg-white/95 p-3">
      <div className="w-full rounded-2xl border border-blue-100 bg-blue-50 p-4 text-center shadow-sm">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-blue-100 text-blue-700">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="h-7 w-7" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574v7.652c0 1.067.75 1.994 1.802 2.169 1.961.327 3.95.532 5.948.608m0 0c2.003-.076 3.992-.281 5.953-.608 1.052-.175 1.797-1.102 1.797-2.169v-7.652c0-1.067-.745-1.994-1.797-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-2.343 0 2.192 2.192 0 00-1.736 1.039l-.822 1.316z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM18.75 8.25h.008v.008h-.008V8.25zM12 18.75v2.25m-3.75 0h7.5" />
          </svg>
        </div>
        <h2 className="text-base font-bold text-slate-900">Grant Access to Continue</h2>
        <p className="mx-auto mt-1 max-w-sm text-sm leading-5 text-slate-600">
          Please allow access to your camera and microphone when prompted by your browser to proceed with the assessment.
        </p>
        <div className="mt-4 flex flex-col gap-2 text-left">
          <PermissionRequirement type="camera" status={camStatus} onRetry={onRetryCamera} />
          <PermissionRequirement type="microphone" status={micStatus} onRetry={onRetryMic} />
        </div>
      </div>
    </div>
  );
}

function Test() {
  const webcamRef = React.useRef();
  const mountedRef = React.useRef(false);
  const camRequestIdRef = React.useRef(0);
  const micRequestIdRef = React.useRef(0);
  const initialMediaRequestStartedRef = React.useRef(false);
  const cameraProbeStreamRef = React.useRef(null);
  const micProbeStreamRef = React.useRef(null);
  const wsRef = React.useRef(null);
  const read = useSnap();
  const [mics, setMics] = React.useState([]);
  const [webcams, setWebcams] = React.useState([]);
  const [openModal, setOpenModal] = React.useState('');
  const [camStatus, setCamStatus] = useState('idle');   // PermissionStatus
  const [micStatus, setMicStatus] = useState('idle');   // PermissionStatus

  const stopStream = React.useCallback((stream) => {
    if (!stream || typeof stream.getTracks !== 'function') {
      return;
    }
    stream.getTracks().forEach((track) => {
      if (track && typeof track.stop === 'function') {
        track.stop();
      }
    });
  }, []);

  const clearProbeStreams = React.useCallback(() => {
    stopStream(cameraProbeStreamRef.current);
    stopStream(micProbeStreamRef.current);
    cameraProbeStreamRef.current = null;
    micProbeStreamRef.current = null;
    write.stream = '';
  }, [stopStream]);

  const canUseMediaDevices = React.useCallback(() => (
    Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
  ), []);

  const enumerateDevicesSafely = React.useCallback(async () => {
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return [];
    }
    return navigator.mediaDevices.enumerateDevices();
  }, []);

  const isCurrentRequest = React.useCallback((type, requestId) => {
    if (!mountedRef.current) {
      return false;
    }
    return type === 'cam'
      ? camRequestIdRef.current === requestId
      : micRequestIdRef.current === requestId;
  }, []);

  React.useEffect(() => {
    wsRef.current = read.ws;
  }, [read.ws]);

  React.useEffect(() => {
    mountedRef.current = true;
    write.OS = getMobileOperatingSystem();

    const timeoutID = window.setTimeout(() => {
      write.page = 'DC'
      wsRef.current && wsRef.current.close && wsRef.current.close() //? if the user doesn't enter the exam, it will disconnect them from our server within 60 mins.
      clearInterval(micTestInterval)
    }, 3600000);
    return () => {
      mountedRef.current = false;
      camRequestIdRef.current += 1;
      micRequestIdRef.current += 1;
      window.clearTimeout(timeoutID);
      clearProbeStreams();
    };
  }, [clearProbeStreams]);

  // console.log(read.OS, "🪠🪠🪠");

  // React.useEffect(() => {
  //     if(read.OS === 'PC'){
  //         navigator.mediaDevices.getDisplayMedia({ video: true, audio: false }).then((sStream) => { //! should be activated later
  //          const sharedScreenName = sStream.getVideoTracks()[0].getSettings().displaySurface
  //                  write.sStream = ref(sStream)
  //    })
  //     }
  // }, [read.OS]);



  // console.log(read.activeMic, read.activeWebcam)

  // React.useEffect(() => {
  //   let visibilityHidden, visibilityChange;
  //   // Opera 12.10 and Firefox 18 and later support
  //   if (typeof document.hidden !== "undefined") {
  //     visibilityHidden = "hidden";
  //     visibilityChange = "visibilitychange";
  //   } else if (typeof document.mozHidden !== "undefined") {
  //     visibilityHidden = "mozHidden";
  //     visibilityChange = "mozvisibilitychange";
  //   } else if (typeof document.msHidden !== "undefined") {
  //     visibilityHidden = "msHidden";
  //     visibilityChange = "msvisibilitychange";
  //   } else if (typeof document.webkitHidden !== "undefined") {
  //     visibilityHidden = "webkitHidden";
  //     visibilityChange = "webkitvisibilitychange";
  //   }

  //   function fn() {
  //     if (document[visibilityHidden]) {
  //       write.tabControl++
  //     }
  //   }

  //   document.addEventListener(visibilityChange, fn)
  //   return () => window.removeEventListener(visibilityChange, fn);
  // }, []);


  // console.log('🙈🙈🙈🙈', read.tabControl)



  /**
   * Handle device change events
   */
  const handleDeviceChange = React.useCallback(async () => {
    if (!read.activeMic) {
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
      return;
    }

    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioDevices = devices.filter((d) => d.kind === 'audioinput');
      const selectedDeviceExists = audioDevices.some((d) => d.deviceId === read.activeMic);

      if (!selectedDeviceExists) {
        toast.error("Selected microphone is no longer available. Please select a different microphone.", {
          duration: Infinity,
          id: "device-gone",
        });
        setMicStatus('no_device');
        write.micAndCamState = false;
        clearInterval(micTestInterval);
      }
    } catch (error) {
      console.error('Error handling device change:', error);
    }
  }, [read.activeMic]);

  /**
   * Passive microphone permission check.
   * Requests mic access, validates the stream has a live audio track,
   * stores the stream in write.stream, and sets up track/device monitoring.
   * No AudioContext, no canvas visualization, no speech detection.
   */
  const requestMicPermission = React.useCallback(async (deviceId = write.activeMic) => {
    const requestId = micRequestIdRef.current + 1;
    micRequestIdRef.current = requestId;
    stopStream(micProbeStreamRef.current);
    micProbeStreamRef.current = null;
    write.stream = '';
    setMicStatus('pending');

    // Check for browser support
    if (!canUseMediaDevices()) {
      setMicStatus('unsupported');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: deviceId ? deviceId : undefined },
      });

      if (!isCurrentRequest('mic', requestId)) {
        stopStream(stream);
        return;
      }

      micProbeStreamRef.current = stream;
      const audioTracks = stream.getAudioTracks();
      const track = audioTracks[0];

      if (!track || !validateAudioTrack(track)) {
        // Stream obtained but no live audio track — stop tracks and report error
        stopStream(stream);
        micProbeStreamRef.current = null;
        setMicStatus('error');
        return;
      }

      // Store the stream for downstream recording components
      stopStream(stream);
      micProbeStreamRef.current = null;
      write.stream = '';
      setMicStatus('granted');

      // Listen for device changes (e.g. mic unplugged)
      if (navigator.mediaDevices.addEventListener) {
        navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);
      }

      // Enumerate devices to populate mic selector
      try {
        const devices = await enumerateDevicesSafely();
        if (!isCurrentRequest('mic', requestId)) {
          return;
        }
        const { mics: foundMics } = filterDevices(devices);
        setMics(foundMics);
      } catch (_enumErr) {
        // Device enumeration failure is non-fatal
      }
    } catch (err) {
      if (isCurrentRequest('mic', requestId)) {
        setMicStatus(mapErrorToStatus(err.name));
      }
    }
  }, [canUseMediaDevices, enumerateDevicesSafely, handleDeviceChange, isCurrentRequest, stopStream]);

  /**
   * Request camera permission and update camStatus accordingly.
   * Sets up camPerm.onchange to redirect to CAMMICOFF if permission is revoked.
   * Enumerates devices on success to populate webcams state.
   */
  const requestCameraPermission = React.useCallback(async (deviceId = write.activeWebcam) => {
    const requestId = camRequestIdRef.current + 1;
    camRequestIdRef.current = requestId;
    stopStream(cameraProbeStreamRef.current);
    cameraProbeStreamRef.current = null;
    setCamStatus('pending');

    // Check for browser support
    if (!canUseMediaDevices()) {
      setCamStatus('unsupported');
      return;
    }

    const videoConstraints = {
      width: (write.OS === 'iOS' || write.OS === 'Android') ? 640 : 1280,
      height: (write.OS === 'iOS' || write.OS === 'Android') ? 480 : 720,
      facingMode: 'user',
      deviceId: deviceId ? deviceId : undefined,
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: videoConstraints });

      if (!isCurrentRequest('cam', requestId)) {
        stopStream(stream);
        return;
      }

      cameraProbeStreamRef.current = stream;
      setCamStatus('granted');

      // Stop the test stream. The preview and Rec request their own streams.
      stopStream(stream);
      cameraProbeStreamRef.current = null;

      // Set up permission change listener to detect revocation after grant
      try {
        if (navigator.permissions && navigator.permissions.query) {
          const camPerm = await navigator.permissions.query({ name: 'camera' });
          camPerm.onchange = () => {
            write.page = 'CAMMICOFF';
          };
        }
      } catch (_permErr) {
        // navigator.permissions.query may not be supported in all browsers; safe to ignore
      }

      // Stop the test stream — the <Webcam> component will manage its own stream
      // Enumerate devices to populate webcam selector
      try {
        const devices = await enumerateDevicesSafely();
        if (!isCurrentRequest('cam', requestId)) {
          return;
        }
        const { webcams: foundWebcams } = filterDevices(devices);
        setWebcams(foundWebcams);
      } catch (_enumErr) {
        // Device enumeration failure is non-fatal
      }
    } catch (err) {
      if (isCurrentRequest('cam', requestId)) {
        setCamStatus(mapErrorToStatus(err.name));
      }
    }
  }, [canUseMediaDevices, enumerateDevicesSafely, isCurrentRequest, stopStream]);

  // On mount: request camera and microphone permissions in parallel.
  // requestMicPermission handles track monitoring and devicechange listener setup,
  // replacing the old runIt() logic (AudioContext, canvas visualization, validateAudioPresence).
  React.useEffect(() => {
    if (initialMediaRequestStartedRef.current) {
      return;
    }
    initialMediaRequestStartedRef.current = true;
    requestCameraPermission();
    requestMicPermission();
  }, [requestCameraPermission, requestMicPermission]);

  React.useEffect(() => {
    write.micAndCamState = camStatus === 'granted' && micStatus === 'granted';
  }, [camStatus, micStatus]);

  const handleDevices = React.useCallback((mediaDevices) => {
    setWebcams(mediaDevices.filter(({ kind }) => kind === "videoinput"));
    setMics(mediaDevices.filter(({ kind }) => kind === "audioinput"));
    // console.log(`mediaDevices` , mediaDevices)
  }, []);

  React.useEffect(() => {
    if (!read.activeWebcam && navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
      navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }
  }, [read.activeWebcam, handleDevices, read.activeMic, read.micAndCamState]);

  function handleSelect(e) {
    if (e.target.getAttribute("about") === "cam") {
      clearInterval(micTestInterval);
      write.activeWebcam = e.target.value;
      requestCameraPermission(e.target.value);
    }
    if (e.target.getAttribute("about") === "mic") {
      clearInterval(micTestInterval);
      write.activeMic = e.target.value;
      requestMicPermission(e.target.value);
    }
  }

  function goToRec() {
    clearInterval(micTestInterval);
    camRequestIdRef.current += 1;
    micRequestIdRef.current += 1;
    clearProbeStreams();
    posthog.capture('exam_started', {
      invitation_id: read.id,
      os: read.OS,
      total_questions: read.totalQuestions,
      language: read.user.language,
      company: read.user.company,
    });
    write.page = "QUESTIONS";
  }

  React.useEffect(() => {
    return () => {
      clearInterval(micTestInterval);
      clearProbeStreams();
      if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
        navigator.mediaDevices.removeEventListener('devicechange', handleDeviceChange);
      }
    };
  }, [clearProbeStreams, handleDeviceChange]);

  //  React.useEffect(() => { //! SHOULD BE REMOVED LATER ON
  //   setTimeout(() => {
  //     goToRec()
  //   }, 1111);
  //  }, []);




  function handleModal(what) {
    write.instruction[what] = false
    setOpenModal(what)
    if (what === 'tos1' || what === 'tos2') {
      posthog.capture('tos_accepted', {
        invitation_id: read.id,
        tos_type: what,
      });
    }
  }


  // console.log(read.micAndCamState, read.stream.active, '🍧🍧🍧')

  const showMediaAccessOverlay = camStatus !== 'granted' || micStatus !== 'granted';


  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />

      {/* Content column — centered, padded top and bottom to breathe within the App scroll container */}
      <div className="test-page-shell flex w-full flex-col items-center px-4 py-4 sm:px-6 sm:py-6">

        {/* Header */}
        <div className="mb-3 flex w-full max-w-md flex-col items-center text-center">
          <h1 className="max-w-full break-words text-xl font-extrabold tracking-tight text-slate-950 sm:text-2xl">
            Welcome, {read.user.name}
          </h1>
        </div>

        <div className={`relative mb-3 w-full max-w-md ${showMediaAccessOverlay ? 'min-h-[380px]' : ''}`}>
          {/* Single card containing both Camera and Microphone sections */}
          <div className={`w-full overflow-hidden rounded-[1.75rem] border border-white/70 bg-white shadow-xl shadow-slate-900/10 transition-opacity ${showMediaAccessOverlay ? 'opacity-0 pointer-events-none' : 'opacity-100'}`} aria-hidden={showMediaAccessOverlay}>

          {/* ── Camera section ── */}
          <div className="min-w-0 p-3 sm:p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* camera icon in a soft blue circle */}
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-4 h-4" aria-hidden="true">
                    <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
                  </svg>
                </span>
                <span className="font-bold text-slate-800">Camera</span>
              </div>
              <StatusBadge status={camStatus} label="Camera" />
            </div>

            <StatusIndicator status={camStatus} label="Camera" />

            {camStatus === 'granted' && (
              <Webcam
                ref={webcamRef}
                className="test-webcam-preview mt-2 aspect-video w-full rounded-2xl bg-slate-950 object-cover"
                audio={false}
                muted={true}
                videoConstraints={{
                  width: (read.OS === 'iOS' || read.OS === 'Android') ? 640 : 1280,
                  height: (read.OS === 'iOS' || read.OS === 'Android') ? 480 : 720,
                  facingMode: "user",
                  deviceId: read.activeWebcam ? read.activeWebcam : undefined,
                }}
              />
            )}

            {recoverableStatuses.includes(camStatus) && (
              <button
                onClick={requestCameraPermission}
                className="mt-3 min-h-[44px] w-full rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 sm:w-auto"
              >
                Try Again
              </button>
            )}

            {read.OS === 'PC' && (
              <DeviceSelector
                devices={webcams}
                selected={read.activeWebcam}
                about="cam"
                onChange={handleSelect}
              />
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100" />

          {/* ── Microphone section ── */}
          <div className="min-w-0 p-3 sm:p-4">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {/* mic icon in a soft blue circle */}
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" className="w-4 h-4" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                </span>
                <span className="font-bold text-slate-800">Microphone</span>
              </div>
              <StatusBadge status={micStatus} label="Microphone" />
            </div>

            <StatusIndicator status={micStatus} label="Microphone" />

            {recoverableStatuses.includes(micStatus) && (
              <button
                onClick={requestMicPermission}
                className="mt-3 min-h-[44px] w-full rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-200 sm:w-auto"
              >
                Try Again
              </button>
            )}

            {read.OS === 'PC' && (
              <DeviceSelector
                devices={mics}
                selected={read.activeMic}
                about="mic"
                onChange={handleSelect}
              />
            )}
          </div>
        </div>

        {/* ToS checkboxes — centered */}
        {showMediaAccessOverlay && (
          <MediaAccessOverlay
            camStatus={camStatus}
            micStatus={micStatus}
            onRetryCamera={requestCameraPermission}
            onRetryMic={requestMicPermission}
          />
        )}
        </div>

        <div className="mb-3 flex w-full max-w-md flex-col gap-2.5">
          <label className="flex min-h-[52px] cursor-pointer items-start gap-3 rounded-2xl border border-white/70 bg-white/90 p-3 text-left shadow-sm shadow-slate-900/5 transition-colors hover:bg-white" onClick={() => handleModal('tos1')}>
            <input
              id="tos1-checkbox"
              type="checkbox"
              onChange={() => null}
              checked={read.instruction.tos1}
              className="mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="min-w-0 text-sm font-semibold leading-6 text-slate-700">
              {locale[read.user.language].button.agree}{" "}
              <button
                onClick={(e) => { e.stopPropagation(); handleModal('tos1'); }}
                className="font-bold text-blue-700 underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {locale[read.user.language].button.terms}
              </button>
            </span>
          </label>

          <label className="flex min-h-[52px] cursor-pointer items-start gap-3 rounded-2xl border border-white/70 bg-white/90 p-3 text-left shadow-sm shadow-slate-900/5 transition-colors hover:bg-white" onClick={() => handleModal('tos2')}>
            <input
              id="tos2-checkbox"
              type="checkbox"
              onChange={() => null}
              checked={read.instruction.tos2}
              className="mt-0.5 h-5 w-5 flex-shrink-0 cursor-pointer rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="min-w-0 text-sm font-semibold leading-6 text-slate-700">
              {locale[read.user.language].button.consent}{" "}
              <button
                onClick={(e) => { e.stopPropagation(); handleModal('tos2'); }}
                className="font-bold text-blue-700 underline-offset-4 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {locale[read.user.language].button.usage}
              </button>
            </span>
          </label>
        </div>

        {openModal && <NewModal type={openModal} />}
        {camStatus === 'granted' && micStatus === 'granted' && (
          <NewModal type="welcome" title="General Instruction" />
        )}

        {/* Start button — full width, centered */}
        <div className="w-full max-w-md">
          <button
            type="button"
            disabled={!isNextEnabled(camStatus, micStatus, read.instruction.tos1, read.instruction.tos2, read.instruction.welcome)}
            onClick={goToRec}
            title={
              !isNextEnabled(camStatus, micStatus, read.instruction.tos1, read.instruction.tos2, read.instruction.welcome)
                ? [
                    camStatus !== 'granted' && 'Camera access required',
                    micStatus !== 'granted' && 'Microphone access required',
                    !read.instruction.tos1 && 'Please accept the Terms of Service',
                    !read.instruction.tos2 && 'Please accept the Data Usage consent',
                    !read.instruction.welcome && 'Please read the general instructions',
                  ].filter(Boolean).join(' · ')
                : 'Ready to start!'
            }
            className="flex min-h-[52px] w-full items-center justify-center gap-2 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3.5 text-center text-sm font-black uppercase tracking-[0.14em] text-blue-800 shadow-lg shadow-blue-600/10 transition-colors
              disabled:cursor-not-allowed disabled:bg-blue-50 disabled:text-blue-800 disabled:shadow-blue-600/10
              enabled:bg-blue-600 enabled:text-white enabled:hover:bg-blue-700 enabled:active:bg-blue-800 enabled:focus:outline-none enabled:focus:ring-4 enabled:focus:ring-blue-200"
          >
            {isNextEnabled(camStatus, micStatus, read.instruction.tos1, read.instruction.tos2, read.instruction.welcome)
              ? locale[read.user.language].button.start
              : 'Agree with the terms above to start the test'}
            {isNextEnabled(camStatus, micStatus, read.instruction.tos1, read.instruction.tos2, read.instruction.welcome) && (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4" aria-hidden="true">
                <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>


      </div>
    </>
  );
}

export default Test;
